/**
 * Sprint 26.0 — Add reference modal.
 *
 * Two stacked actions (reusing the 25.2 TriageModal chrome):
 *   1. Reference an existing page — searchable picker of workspace pages.
 *   2. Create a new page and reference it — creates a blank page under the
 *      agent, references it, and opens the page editor.
 * Removal is handled inline in ReferencesSection (this modal is add-only). Every
 * link is a `page`-kind task reference via add_task_reference.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { mcpClient } from "../../../lib/mcp-client";
import { useAddTaskReference } from "../../hooks/use-task-detail";

type PageHit = { id: string; name: string; surface: string | null; status: string | null; snippet: string };

export interface AddReferenceModalProps {
  taskId: string;
  agentId: string;
  onClose: () => void;
}

export function AddReferenceModal({ taskId, agentId, onClose }: AddReferenceModalProps) {
  const navigate = useNavigate();
  const addReference = useAddTaskReference();
  const [mode, setMode] = useState<"choose" | "pick" | "create">("choose");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<PageHit[]>([]);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Debounced page search in pick mode.
  useEffect(() => {
    if (mode !== "pick") return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const results = await mcpClient.searchPages(query, 20);
        if (!cancelled) setHits(results);
      } catch {
        if (!cancelled) setHits([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [mode, query]);

  async function referenceExisting(hit: PageHit) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await addReference.mutateAsync({ taskId, kind: "page", targetId: hit.id });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't reference that page. Please try again.");
      setBusy(false);
    }
  }

  async function createAndReference() {
    if (busy || newTitle.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const node = await mcpClient.createPage({ name: newTitle.trim(), parent_id: agentId, surface: "notes" });
      await addReference.mutateAsync({ taskId, kind: "page", targetId: node.id });
      onClose();
      navigate(`/library/${node.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't create and reference the page. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div style={backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={dialogRef} style={dialog} role="dialog" aria-modal="true" aria-label="Add reference">
        <div style={titleRow}>
          <span style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>Add a reference</span>
          <button type="button" style={iconBtn} aria-label="Close" title="Close" onClick={onClose}>
            <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden="true" />
          </button>
        </div>

        {error && (
          <div style={errorBox} role="alert" data-testid="reference-error">
            <i className="ti ti-alert-triangle" style={{ fontSize: 13 }} aria-hidden="true" /> {error}
          </div>
        )}

        {mode === "choose" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button type="button" style={actionBtn} onClick={() => setMode("pick")}>
              <i className="ti ti-link" style={{ fontSize: 16 }} aria-hidden="true" />
              <span>Reference an existing page</span>
            </button>
            <button type="button" style={actionBtn} onClick={() => setMode("create")}>
              <i className="ti ti-plus" style={{ fontSize: 16 }} aria-hidden="true" />
              <span>Create a new page and reference it</span>
            </button>
          </div>
        )}

        {mode === "pick" && (
          <div>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pages…"
              style={search}
            />
            <div style={hitList}>
              {hits.length === 0 ? (
                <div style={emptyHit}>No matching pages.</div>
              ) : (
                hits.map((h) => (
                  <button key={h.id} type="button" style={hitRow} disabled={busy} onClick={() => referenceExisting(h)}>
                    <i className="ti ti-file-text" style={{ fontSize: 15, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
                    <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>{h.name}</span>
                      {(h.surface || h.status) && (
                        <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{[h.surface, h.status].filter(Boolean).join(" · ")}</span>
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {mode === "create" && (
          <div>
            <input
              autoFocus
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") createAndReference(); }}
              placeholder="New page title…"
              style={search}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button type="button" style={ghostBtn} onClick={() => setMode("choose")}>Back</button>
              <button type="button" style={primaryBtn} disabled={busy || newTitle.trim().length === 0} onClick={createAndReference}>
                Create &amp; reference
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.32)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10000,
};

const dialog: CSSProperties = {
  width: 480,
  maxWidth: "calc(100vw - 32px)",
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-lg)",
  padding: "20px 24px",
  boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
};

const titleRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 16,
};

const iconBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  padding: 4,
};

const actionBtn: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "12px 14px",
  fontSize: 14,
  fontWeight: 500,
  textAlign: "left",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
  cursor: "pointer",
};

const search: CSSProperties = {
  width: "100%",
  padding: "9px 11px",
  fontSize: 13,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  outline: "none",
  boxSizing: "border-box",
};

const hitList: CSSProperties = {
  marginTop: 10,
  maxHeight: 280,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const hitRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "9px 11px",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  background: "transparent",
  cursor: "pointer",
};

const errorBox: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 12,
  padding: "8px 11px",
  fontSize: 12,
  borderRadius: "var(--border-radius-md)",
  background: "rgba(220,38,38,0.10)",
  color: "var(--color-text-danger)",
};

const emptyHit: CSSProperties = {
  fontSize: 12,
  fontStyle: "italic",
  color: "var(--color-text-tertiary)",
  padding: "12px 4px",
};

const ghostBtn: CSSProperties = {
  padding: "7px 14px",
  fontSize: 12,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
};

const primaryBtn: CSSProperties = {
  padding: "7px 14px",
  fontSize: 12,
  fontWeight: 500,
  border: "none",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
};
